import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db } from '../config/firebase';
import app from '../config/firebase';
import { signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, createUserWithEmailAndPassword, signInWithCustomToken, signOut, sendPasswordResetEmail, ActionCodeSettings, setPersistence, browserLocalPersistence, indexedDBLocalPersistence, onIdTokenChanged, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { UserProfile } from '../types/auth';
import trialService from '../services/trialService';
import { handleFirestoreError, isNetworkError } from '../utils/firestoreErrorHandler';
import { trackCompleteRegistration, MetaSignupMethod } from '../utils/metaPixel';
import {
  getAttributionForFirestore,
  getUtmContent,
  trackGa4SignUp,
} from '../utils/attribution';
import {
  getDefaultPermissionsByRole,
  resolveUserPermissions,
} from '../utils/resolveUserPermissions';

interface AuthContextType {
  currentUser: UserProfile | null;
  loading: boolean;
  userRole: string | null;
  userPermissions: string[];
  clinicId: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginByMatricula: (matricula: string, password: string) => Promise<void>;
  loginWithGoogle: (options?: { emailHint?: string }) => Promise<UserProfile | null>;
  loginWithFacebook: () => Promise<void>;
  loginWithMicrosoft: (options?: { emailHint?: string }) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getUserRedirectUrl: () => string;
  hasPermission: (permission: string) => boolean;
  refreshUserPermissions: () => Promise<void>;
  isAdmin: () => boolean;
  isProfessional: () => boolean;
  isPatient: () => boolean;
  resetPassword: (email: string) => Promise<void>;
  trialData: any; // Adicionado trialData
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function useAuthContext() {
  return useContext(AuthContext);
}

// Constantes para controle de sessão
const INACTIVITY_TIMEOUT = 3 * 60 * 60 * 1000; // 3 horas em milissegundos
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Verificar a cada 1 minuto
const STORAGE_KEY_LAST_ACTIVITY = 'upclinic_last_activity';
const STORAGE_KEY_SESSION_START = 'upclinic_session_start';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [trialData, setTrialData] = useState<any>(null);
  const redirectProcessedRef = useRef(false); // Flag para evitar processamento duplicado de redirect
  const loginInProgressRef = useRef(false); // Flag para prevenir múltiplos logins simultâneos
  const activityCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityListenersRef = useRef<boolean>(false);


  // Função para verificar permissões
  const hasPermission = (permission: string): boolean => {
    // Admin tem todas as permissões
    if (userPermissions.includes('all')) {
      return true;
    }
    
    // Verificar se a permissão específica está na lista
    return userPermissions.includes(permission);
  };
  
  // Função para verificar permissões de billing
  const hasBillingPermission = (permission: string): boolean => {
    if (userPermissions.includes('all')) {
      return true;
    }
    return userPermissions.includes(`billing.${permission}`) || userPermissions.includes('billing.*');
  };
  
  // Função para verificar permissões de IA
  const hasAIPermission = (permission: string): boolean => {
    if (userPermissions.includes('all')) {
      return true;
    }
    return userPermissions.includes(`ai.${permission}`) || userPermissions.includes('ai.*');
  };

  // Funções de verificação de roles
  const isAdmin = (): boolean => userRole === 'admin';
  const isProfessional = (): boolean => userRole === 'professional';
  const isPatient = (): boolean => userRole === 'patient';

  /** Fallback síncrono (defaults do role) — sem Firestore. */
  const setPermissionsByRole = (role: string) => {
    console.log('🔐 [AUTH] Definindo permissões (fallback) para role:', role);
    let normalizedRole = role;
    const validRoles = ['admin', 'professional', 'secretary', 'assistant', 'intern', 'patient', 'financeiro', 'recepcao'];
    if (!validRoles.includes(role)) {
      console.warn(`⚠️ [AUTH] Role inválido: "${role}". Usando "professional"`);
      // Só normaliza localmente: o papel gravado no perfil é controlado pelo servidor/admin.
      normalizedRole = 'professional';
    }
    const permissions = getDefaultPermissionsByRole(normalizedRole);
    console.log('✅ [AUTH] Permissões fallback:', permissions);
    setUserPermissions(permissions);
  };

  /** Carrega permissões reais (clinic_system_users / roles / legado). */
  const applyResolvedPermissions = async (params: {
    uid: string;
    email?: string | null;
    clinicId?: string | null;
    role?: string | null;
  }): Promise<void> => {
    try {
      const permissions = await resolveUserPermissions({
        uid: params.uid,
        email: params.email,
        clinicId: params.clinicId,
        usersRole: params.role,
      });
      console.log('✅ [AUTH] Permissões resolvidas:', permissions);
      setUserPermissions(permissions);
    } catch (error) {
      console.warn('⚠️ [AUTH] Falha ao resolver permissões, usando fallback do role:', error);
      setPermissionsByRole(params.role || 'professional');
    }
  };

  const refreshUserPermissions = async (): Promise<void> => {
    const uid = auth.currentUser?.uid;
    if (!uid || !userRole) return;
    await applyResolvedPermissions({
      uid,
      email: currentUser?.email || auth.currentUser?.email,
      clinicId: clinicId || currentUser?.clinicId,
      role: userRole,
    });
  };

  // Função para resetar senha
  const resetPassword = async (email: string) => {
    try {
      // Configurar URL de redirecionamento após reset de senha
      // IMPORTANTE: A URL deve estar na lista de domínios autorizados no Firebase Console
      // Authentication > Settings > Authorized domains
      const actionCodeSettings: ActionCodeSettings = {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false, // false = Firebase redireciona para nossa URL (melhor para produção)
      };

      console.log('📧 [RESET-PASSWORD] Enviando email de recuperação para:', email);
      console.log('📧 [RESET-PASSWORD] URL de redirecionamento:', actionCodeSettings.url);
      console.log('🇧🇷 [RESET-PASSWORD] Idioma configurado:', auth.languageCode);
      
      // Garantir que o idioma está configurado antes de enviar
      if (auth.languageCode !== 'pt-BR') {
        auth.languageCode = 'pt-BR';
        console.log('🇧🇷 [RESET-PASSWORD] Idioma atualizado para pt-BR');
      }
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      
      console.log('✅ [RESET-PASSWORD] Email de recuperação enviado com sucesso');
    } catch (error: any) {
      console.error('❌ [RESET-PASSWORD] Erro ao enviar email de recuperação:', error);
      
      // Melhorar mensagens de erro
      if (error.code === 'auth/user-not-found') {
        throw new Error('Email não encontrado. Verifique se o email está correto.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Email inválido. Verifique o formato do email.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
      } else {
        throw new Error(error.message || 'Erro ao enviar email de recuperação. Tente novamente mais tarde.');
      }
    }
  };

  // Throttle para logs de atividade (reduz spam no console)
  const lastLogTimeRef = React.useRef(0);
  const LOG_THROTTLE_MS = 60000; // Log apenas uma vez por minuto
  
  // Função para atualizar timestamp de última atividade
  const updateLastActivity = () => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.toString());
    
    // Throttle: log apenas uma vez por minuto para não encher o console
    if (now - lastLogTimeRef.current > LOG_THROTTLE_MS) {
      console.log('🔄 [AUTH] Atividade atualizada:', new Date(now).toLocaleString('pt-BR'));
      lastLogTimeRef.current = now;
    }
  };

  // Função para verificar se a sessão expirou por inatividade
  // Esta função é usada apenas pela verificação periódica (não no onAuthStateChanged)
  const checkSessionExpiry = async (): Promise<boolean> => {
    const lastActivityStr = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
    if (!lastActivityStr) {
      // Sem timestamp = sessão nova, não expirar
      return false;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    if (isNaN(lastActivity)) {
      // Timestamp inválido, limpar e permitir acesso
      localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
      return false;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    // Usar margem de segurança de 5 minutos para evitar falsos positivos
    const SAFETY_MARGIN = 5 * 60 * 1000;
    
    if (timeSinceLastActivity > (INACTIVITY_TIMEOUT + SAFETY_MARGIN)) {
      console.log('⏰ [AUTH] Sessão expirada por inatividade (mais de 3 horas)');
      console.log(`⏰ [AUTH] Última atividade: ${new Date(lastActivity).toLocaleString('pt-BR')}`);
      console.log(`⏰ [AUTH] Tempo sem atividade: ${Math.round(timeSinceLastActivity / (1000 * 60 * 60))} horas`);
      await handleSessionExpiry();
      return true;
    }

    return false;
  };

  // Função para lidar com expiração de sessão
  const handleSessionExpiry = async () => {
    console.log('🔒 [AUTH] Encerrando sessão por inatividade...');
    await logout();
    // Limpar dados de sessão
    localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEY_SESSION_START);
  };

  // Configurar listeners de atividade do usuário
  const setupActivityListeners = () => {
    if (activityListenersRef.current) return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const updateActivity = () => updateLastActivity();

    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    activityListenersRef.current = true;
    console.log('👂 [AUTH] Listeners de atividade configurados');
  };

  // Remover listeners de atividade
  const removeActivityListeners = () => {
    if (!activityListenersRef.current) return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const updateActivity = () => updateLastActivity();

    events.forEach(event => {
      document.removeEventListener(event, updateActivity);
    });

    activityListenersRef.current = false;
    console.log('🔇 [AUTH] Listeners de atividade removidos');
  };

  async function login(email: string, password: string) {
    try {
      // Validar email antes de tentar login
      if (!email || !email.trim()) {
        throw new Error('Email é obrigatório');
      }
      
      if (!password || !password.trim()) {
        throw new Error('Senha é obrigatória');
      }
      
      // Normalizar email (trim e lowercase)
      const normalizedEmail = email.trim().toLowerCase();

      const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      
      console.log('✅ [AUTH] Autenticação Firebase bem-sucedida, UID:', result.user.uid);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        
        if (!userDoc.exists()) {
          console.warn('⚠️ [AUTH] Usuário autenticado mas não encontrado na coleção users');
          throw new Error('Conta não encontrada. Entre em contato com o suporte.');
        }
      
      const userData = userDoc.data() as UserProfile;
      setCurrentUser({ ...userData, uid: result.user.uid });
      setUserRole(userData.role);
      setClinicId(userData.clinicId);

      await applyResolvedPermissions({
        uid: result.user.uid,
        email: userData.email || result.user.email,
        clinicId: userData.clinicId,
        role: userData.role,
      });
      
      // Salvar timestamp de início de sessão e última atividade
      const now = Date.now();
      localStorage.setItem(STORAGE_KEY_SESSION_START, now.toString());
      localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.toString());
      
      // Configurar listeners de atividade
      setupActivityListeners();
      
        // Iniciar verificação periódica de expiração
        startActivityCheck();
        
        console.log('✅ [AUTH] Login realizado com persistência de sessão');
      } catch (firestoreError: any) {
        // Tratar erros do Firestore durante login
        handleFirestoreError(firestoreError, 'AUTH-LOGIN', {
          suppressNetworkErrors: false, // Não suprimir durante login - é crítico
        });
        
        // Re-throw para ser tratado pelo componente de login
        throw firestoreError;
      }
    } catch (error: any) {
      console.error('❌ [AUTH] Erro no login:', error);
      
      // Preservar o código de erro do Firebase para tratamento específico
      if (error.code) {
        const firebaseError = new Error(error.message || 'Erro ao fazer login');
        (firebaseError as any).code = error.code;
        throw firebaseError;
      }
      
      throw error;
    }
  }

  // Iniciar verificação periódica de atividade
  const startActivityCheck = () => {
    // Limpar intervalo anterior se existir
    if (activityCheckIntervalRef.current) {
      clearInterval(activityCheckIntervalRef.current);
    }

    // Verificar a cada minuto se a sessão expirou
    // A verificação usa localStorage diretamente, não depende de currentUser
    activityCheckIntervalRef.current = setInterval(async () => {
      const lastActivityStr = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
      if (lastActivityStr) {
        await checkSessionExpiry();
      }
    }, ACTIVITY_CHECK_INTERVAL);
  };

  // Parar verificação de atividade
  const stopActivityCheck = () => {
    if (activityCheckIntervalRef.current) {
      clearInterval(activityCheckIntervalRef.current);
      activityCheckIntervalRef.current = null;
    }
  };

  async function loginByMatricula(matricula: string, password: string) {
    try {
      // Validação da senha, criação da conta Auth e do perfil users/{uid} acontecem no servidor
      // (loginByEnrollment). O navegador só recebe um token assinado — não cria contas nem grava
      // role/clinicId, que as regras do Firestore bloqueiam para o cliente.
      const callable = httpsCallable<
        { matricula: string; password: string },
        { customToken: string; userProfile: UserProfile }
      >(getFunctions(app, 'us-central1'), 'loginByEnrollment');
      const { data } = await callable({ matricula, password });
      await signInWithCustomToken(auth, data.customToken);

      const now = new Date();
      const userProfile: UserProfile = {
        ...data.userProfile,
        lastLogin: now,
        createdAt: now,
        updatedAt: now,
      };

      setCurrentUser(userProfile);
      setUserRole(userProfile.role || 'professional');
      await applyResolvedPermissions({
        uid: userProfile.uid,
        email: userProfile.email,
        clinicId: userProfile.clinicId,
        role: userProfile.role || 'professional',
      });
      setClinicId(userProfile.clinicId);
      localStorage.setItem('userProfile', JSON.stringify(userProfile));

      localStorage.setItem(STORAGE_KEY_SESSION_START, now.getTime().toString());
      localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.getTime().toString());
      setupActivityListeners();
      startActivityCheck();
    } catch (error: any) {
      console.error('[AUTH] Erro no login por matrícula:', error?.code || error);
      const message =
        typeof error?.message === 'string' && error.message && !error.message.startsWith('internal')
          ? error.message
          : 'Matrícula ou senha incorretos';
      throw new Error(message);
    }
  }

  // Função auxiliar para determinar se deve usar redirect diretamente
  function shouldUseRedirectDirectly(): boolean {
    if (typeof window === 'undefined') return true; // Em SSR, usar redirect por segurança
    
    const isElectron = window.navigator.userAgent.includes('Electron');
    
    // Log para debug
    console.log('[AUTH] Verificando ambiente:', {
      hostname: window.location.hostname,
      isElectron
    });
    
    // Electron → sempre redirect (popup pode ter problemas no Electron)
    if (isElectron) {
      console.log('[AUTH] 🖥️ Ambiente Electron detectado - usando redirect');
      return true;
    }
    
    // Web (localhost E produção) → SEMPRE usar popup
    // Popup funciona melhor, é mais rápido e evita problemas de redirect
    // Esta é a configuração que funcionava antes (documentada em SOLUCAO_APLICADA.md)
    console.log('[AUTH] 🌐 Ambiente web detectado - usando popup');
      return false;
    }
    
  async function loginWithGoogle(options?: { emailHint?: string }): Promise<UserProfile | null> {
    // Prevenir múltiplos logins simultâneos
    if (loginInProgressRef.current) {
      console.log('[AUTH] ⚠️ Login já em andamento, ignorando nova tentativa');
      return null;
    }
    
    // Declarar provider fora do try para estar disponível no catch
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      const googleParams: Record<string, string> = {
        prompt: 'select_account',
      };
      if (options?.emailHint) {
        googleParams.login_hint = options.emailHint.trim().toLowerCase();
      }
      provider.setCustomParameters(googleParams);
      
    try {
      loginInProgressRef.current = true;
      
      const useRedirect = shouldUseRedirectDirectly();
      console.log('[AUTH] 📌 Método selecionado:', useRedirect ? 'REDIRECT' : 'POPUP');
      
      if (useRedirect) {
        console.log('[AUTH] ▶️ Iniciando login com redirect...');
        console.log('[AUTH] 🔗 URL atual antes do redirect:', window.location.href);
        redirectProcessedRef.current = false;
        sessionStorage.setItem('upclinic_auth_redirect', '1');
        
        signInWithRedirect(auth, provider)
          .then(() => {
            console.warn('[AUTH] ⚠️ signInWithRedirect resolveu sem redirecionar - isso não deveria acontecer');
          })
          .catch((error: any) => {
            console.error('[AUTH] ❌ Erro ao iniciar redirect:', error);
            redirectProcessedRef.current = true;
            
            console.log('[AUTH] 🔄 Redirect falhou, tentando popup como fallback...');
            signInWithPopup(auth, provider)
              .then((result) => {
                console.log('[AUTH] ✅ Popup fallback bem-sucedido');
                redirectProcessedRef.current = false;
                return processAuthResult(result.user, 'google');
              })
              .catch((popupError: any) => {
                console.error('[AUTH] ❌ Popup fallback também falhou:', popupError);
                throw popupError;
              });
          });
        
        return null; // Redirect não retorna aqui
      }
      
      // Popup para ambiente web (localhost e produção)
      console.log('[AUTH] 🪟 Iniciando login com popup...');
      
      // Tentar popup com tratamento de erro melhorado
      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError: any) {
        // Capturar erro de COOP antes que seja propagado
        const errorMessage = popupError?.message || '';
        const errorString = JSON.stringify(popupError || {});
        const errorStack = popupError?.stack || '';
        
        // Detectar múltiplos tipos de erro relacionados a popup/COOP
        const isCOOPError = 
          errorMessage.includes('Cross-Origin-Opener-Policy') || 
          errorMessage.includes('COOP') ||
          errorMessage.includes('window.closed') ||
          errorMessage.includes('Pending promise') ||
          errorMessage.includes('INTERNAL ASSERTION') ||
          errorString.includes('Cross-Origin-Opener-Policy') ||
          errorString.includes('COOP') ||
          errorString.includes('window.closed') ||
          errorString.includes('Pending promise') ||
          errorString.includes('INTERNAL ASSERTION') ||
          errorStack.includes('Cross-Origin-Opener-Policy') ||
          errorStack.includes('COOP') ||
          errorStack.includes('window.closed') ||
          errorStack.includes('Pending promise') ||
          errorStack.includes('INTERNAL ASSERTION');
        
        // Usuário fechou o popup — não tratar como COOP nem iniciar redirect (evita "Entrando…" eterno)
        if (popupError?.code === 'auth/popup-closed-by-user') {
          console.log('[AUTH] ℹ️ Usuário cancelou o popup');
          loginInProgressRef.current = false;
          const cancelErr: any = new Error('Login cancelado. Tente novamente.');
          cancelErr.code = 'auth/popup-closed-by-user';
          throw cancelErr;
        }

        // auth/internal-error costuma ocorrer quando gapi/login Google falha (CSP, rede, etc.) — redirect evita depender do popup
        if (popupError?.code === 'auth/internal-error') {
          console.warn('[AUTH] ⚠️ auth/internal-error no popup — tentando login com redirect...');
          loginInProgressRef.current = false;
          try {
            sessionStorage.setItem('upclinic_auth_redirect', '1');
            await signInWithRedirect(auth, provider);
            return null;
          } catch (redirectErr: any) {
            console.error('[AUTH] ❌ Redirect após internal-error:', redirectErr);
            loginInProgressRef.current = false;
            throw popupError;
          }
        }

        // COOP / popup bloqueado → redirect com sinalização para a UI
        if (popupError?.code === 'auth/popup-blocked' || isCOOPError) {
          console.warn('[AUTH] ⚠️ Erro detectado durante popup:', {
            code: popupError?.code,
            message: errorMessage,
            isCOOPError
          });
          console.warn('[AUTH] ⚠️ Usando redirect como fallback...');
          loginInProgressRef.current = false;
          
          try {
            sessionStorage.setItem('upclinic_auth_redirect', '1');
            await signInWithRedirect(auth, provider);
            return null; // Redirect vai redirecionar
          } catch (redirectError: any) {
            console.error('[AUTH] ❌ Erro ao usar redirect também:', redirectError);
            loginInProgressRef.current = false;
            const err: any = new Error(
              'Não foi possível concluir o login com Google. Tente novamente ou use e-mail e senha.'
            );
            err.code = 'auth/redirect-failed';
            throw err;
          }
        }
        
        // Re-lançar outros erros para serem tratados no catch externo
        throw popupError;
      }
      
      // Se chegou aqui, o popup foi bem-sucedido
      console.log('[AUTH] ✅ Popup autenticado, processando usuário...', result.user.email);
      
      // Processar resultado
        await processAuthResult(result.user, 'google');
      
      console.log('[AUTH] ✅ Popup concluído, estado atualizado');
      
      // Retornar o perfil do usuário processado
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (userDoc.exists()) {
        return { ...userDoc.data() as UserProfile, uid: result.user.uid };
      }
      
      return null;
      
    } catch (error: any) {
      console.error('[AUTH] ❌ Erro no popup:', error?.code, error?.message);
      
      // Detectar erros relacionados a COOP e erros internos do Firebase - verificar múltiplas formas
      const errorMessage = error?.message || '';
      const errorString = JSON.stringify(error || {});
      const errorStack = error?.stack || '';
      
      const isCOOPError = 
        errorMessage.includes('Cross-Origin-Opener-Policy') || 
        errorMessage.includes('COOP') ||
        errorMessage.includes('window.closed') ||
        errorMessage.includes('Pending promise') ||
        errorMessage.includes('INTERNAL ASSERTION') ||
        errorString.includes('Cross-Origin-Opener-Policy') ||
        errorString.includes('COOP') ||
        errorString.includes('window.closed') ||
        errorString.includes('Pending promise') ||
        errorString.includes('INTERNAL ASSERTION') ||
        errorStack.includes('Cross-Origin-Opener-Policy') ||
        errorStack.includes('COOP') ||
        errorStack.includes('window.closed') ||
        errorStack.includes('Pending promise') ||
        errorStack.includes('INTERNAL ASSERTION') ||
        errorStack.includes('PopupOperation');
      
      console.log('[AUTH] 🔍 Detecção de erro:', {
        code: error?.code,
        message: errorMessage,
        isCOOPError,
        stack: errorStack.substring(0, 200)
      });
      
      // Usuário cancelou — propagar para a UI limpar "Entrando…"
      if (error?.code === 'auth/popup-closed-by-user') {
        console.log('[AUTH] ℹ️ Usuário cancelou o popup');
        loginInProgressRef.current = false;
        throw error;
      }
      
      // Se popup foi bloqueado ou COOP, usar redirect automaticamente
      if (error?.code === 'auth/popup-blocked' || isCOOPError) {
        console.warn('[AUTH] ⚠️ Erro detectado (popup bloqueado, COOP, ou erro interno):', {
          code: error?.code,
          message: errorMessage.substring(0, 100)
        });
        console.log('[AUTH] 🔄 Usando redirect como fallback automático...');
        
        try {
          loginInProgressRef.current = false;
          sessionStorage.setItem('upclinic_auth_redirect', '1');
          await signInWithRedirect(auth, provider);
          return null;
        } catch (redirectError: any) {
          console.error('[AUTH] ❌ Erro ao iniciar redirect:', redirectError);
          loginInProgressRef.current = false;
          const err: any = new Error(
            'Não foi possível concluir o login com Google. Tente novamente ou use e-mail e senha.'
          );
          err.code = 'auth/redirect-failed';
          throw err;
        }
      }
      
      // Para outros erros, tentar redirect como fallback
      console.log('[AUTH] 🔄 Popup falhou - usando redirect como fallback');
      
      try {
        loginInProgressRef.current = false;
        sessionStorage.setItem('upclinic_auth_redirect', '1');
        await signInWithRedirect(auth, provider);
        return null;
      } catch (redirectError: any) {
        console.error('[AUTH] ❌ Erro ao iniciar redirect:', redirectError);
        loginInProgressRef.current = false;
        throw redirectError;
      }
    } finally {
      // Garantir que a flag seja resetada mesmo se houver erro não tratado
      if (loginInProgressRef.current) {
        loginInProgressRef.current = false;
      }
    }
  }
  
  // Função auxiliar para processar resultado de autenticação
  async function processAuthResult(user: any, provider?: string) {
    console.log('[AUTH] 🔄 Processando resultado de autenticação para:', user.uid);
    
    try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data() as UserProfile;
      console.log('[AUTH] ✅ Usuário encontrado no Firestore, carregando dados...');
      
      // ✅ GARANTIR: Se displayName está vazio, atualizar com dados do Google
      const displayName = user.displayName || 
                         user.email?.split('@')[0] || 
                         'Usuário';
      
      // SEMPRE garantir que displayName existe no userData antes de setar currentUser
      if (!userData.displayName || userData.displayName.trim() === '') {
        console.log('[AUTH] 📝 Atualizando displayName vazio no Firestore');
        await updateDoc(doc(db, 'users', user.uid), {
          displayName: displayName,
          updatedAt: new Date()
        });
        userData.displayName = displayName; // Atualizar também no objeto local
      }
      
      // GARANTIR que displayName está sempre definido no currentUser
      const finalUserData = {
        ...userData,
        uid: user.uid,
        displayName: userData.displayName || displayName // Fallback adicional
      };
      
      console.log('[AUTH] 📝 DisplayName final no currentUser:', finalUserData.displayName);
      
      // Atualizar estado de forma síncrona
      setCurrentUser(finalUserData);
      setUserRole(userData.role);
      await applyResolvedPermissions({
        uid: user.uid,
        email: finalUserData.email || user.email,
        clinicId: userData.clinicId,
        role: userData.role,
      });
      setClinicId(userData.clinicId);
        
      // Salvar timestamp de início de sessão e última atividade
      const now = Date.now();
      localStorage.setItem(STORAGE_KEY_SESSION_START, now.toString());
      localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.toString());
        
      // Configurar listeners de atividade
      setupActivityListeners();
      startActivityCheck();
        
      console.log('[AUTH] ✅ Estado do usuário atualizado:', userData.email);
    } else {
      // Criar novo usuário
        console.log('[AUTH] 📝 Usuário não encontrado, criando novo perfil...');
      
      // Garantir que displayName sempre tenha um valor
      // Se Google não retornar displayName, usar email como fallback
      const displayName = user.displayName || 
                         user.email?.split('@')[0] || 
                         'Usuário';
      
      console.log('[AUTH] 📝 DisplayName do Google:', user.displayName);
      console.log('[AUTH] 📝 DisplayName final (com fallback):', displayName);
      
      const signupMethod: MetaSignupMethod =
          provider === 'google' || provider === 'facebook' || provider === 'microsoft'
            ? provider
            : 'social';
      const attribution = getAttributionForFirestore();
      const newUser: UserProfile = {
        uid: user.uid,
        email: user.email!,
        displayName: displayName,
        role: 'patient',
        clinicId: user.uid, // uid não pode ser forjado; as regras exigem clinicId == uid na criação
        isActive: true,
        lastLogin: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
        attribution,
      };
      await setDoc(doc(db, 'users', user.uid), newUser);
        
        // Atualizar estado de forma síncrona
      setCurrentUser(newUser);
      setUserRole(newUser.role);
      await applyResolvedPermissions({
        uid: user.uid,
        email: newUser.email,
        clinicId: newUser.clinicId,
        role: newUser.role,
      });
      setClinicId(newUser.clinicId);
        
        // Salvar timestamp de início de sessão e última atividade
        const now = Date.now();
        localStorage.setItem(STORAGE_KEY_SESSION_START, now.toString());
        localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.toString());
        
        // Configurar listeners de atividade
        setupActivityListeners();
        startActivityCheck();
        
        // GA4 sign_up (once per uid) + Meta Pixel CompleteRegistration (eventID = uid)
        trackGa4SignUp(user.uid, signupMethod);
        trackCompleteRegistration(user.uid, signupMethod, getUtmContent());
        console.log('[AUTH] ✅ Novo usuário criado e estado atualizado:', newUser.email);
        console.log('[AUTH] ✅ Attribution + sign_up / CompleteRegistration disparados');
      }
      
    } catch (error: any) {
      console.error('[AUTH] ❌ Erro ao processar resultado de autenticação:', error);
      throw error;
    }
  }

  async function loginWithFacebook() {
    try {
      // ✅ Verificar se está em ambiente seguro (HTTPS)
      const isSecure = typeof window !== 'undefined' && 
                       (window.location.protocol === 'https:' || 
                        window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1');
      
      if (!isSecure) {
        const errorMessage = 'O login com Facebook requer uma conexão segura (HTTPS). ' +
                            'Por favor, acesse o site usando HTTPS ou use outro método de login.';
        console.error('[AUTH] ❌ Login Facebook bloqueado - ambiente não seguro:', window.location.protocol);
        throw new Error(errorMessage);
      }

      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      provider.addScope('public_profile');
      
      // Verificar se deve usar redirect diretamente
      if (shouldUseRedirectDirectly()) {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
        console.log('[AUTH] Usando redirect diretamente para Facebook (ambiente: ' + hostname + ')');
        redirectProcessedRef.current = false;
        await signInWithRedirect(auth, provider);
        // O resultado será processado quando a página recarregar via getRedirectResult
        return;
      }
      
      // Tentar popup apenas em localhost ou Electron
      console.log('[AUTH] Tentando popup para Facebook (ambiente local/Electron)');
      try {
        const result = await signInWithPopup(auth, provider);
        redirectProcessedRef.current = false;
        await processAuthResult(result.user, 'facebook');
      } catch (popupError: any) {
        // ✅ Tratar erro específico de ambiente não seguro
        if (popupError?.code === 'auth/invalid-credential-or-provider-id' || 
            popupError?.message?.includes('não seguro') ||
            popupError?.message?.includes('non-secure') ||
            popupError?.message?.includes('HTTPS')) {
          const errorMessage = 'O Facebook bloqueou o login porque a página não está em HTTPS. ' +
                              'Por favor, acesse o site usando HTTPS ou use outro método de login.';
          // Usar console.warn para erro esperado (não é um erro crítico)
          console.warn('[AUTH] ⚠️ Login Facebook bloqueado por segurança (HTTPS requerido):', popupError.message);
          throw new Error(errorMessage);
        }
        
        // Se popup falhar por outro motivo, usar redirect como fallback
        console.log('[AUTH] Popup falhou para Facebook - usando redirect');
        redirectProcessedRef.current = false;
        await signInWithRedirect(auth, provider);
        return;
      }
    } catch (error: any) {
      // Usar console.warn para erros esperados (HTTPS) e console.error para erros reais
      const isExpectedError = 
        error?.message?.includes('não seguro') || 
        error?.message?.includes('non-secure') ||
        error?.message?.includes('HTTPS');
      
      if (isExpectedError) {
        console.warn('[AUTH] ⚠️ Erro esperado no login com Facebook (HTTPS requerido):', error);
      } else {
        console.error('[AUTH] ❌ Erro no login com Facebook:', error);
      }
      
      // ✅ Melhorar mensagem de erro para o usuário
      if (isExpectedError) {
        throw error; // Já tem mensagem clara
      }
      
      // Mensagem genérica melhorada
      if (error?.code === 'auth/invalid-credential-or-provider-id') {
        throw new Error('Erro ao conectar com Facebook. Verifique se está usando HTTPS e tente novamente.');
      }
      
      throw error;
    }
  }

  async function loginWithMicrosoft(options?: { emailHint?: string }) {
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');
      const msParams: Record<string, string> = {
        prompt: 'select_account',
      };
      if (options?.emailHint) {
        msParams.login_hint = options.emailHint.trim().toLowerCase();
      }
      provider.setCustomParameters(msParams);
      
      // Verificar se deve usar redirect diretamente
      if (shouldUseRedirectDirectly()) {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
        console.log('[AUTH] Usando redirect diretamente para Microsoft (ambiente: ' + hostname + ')');
        redirectProcessedRef.current = false;
        await signInWithRedirect(auth, provider);
        // O resultado será processado quando a página recarregar via getRedirectResult
        return;
      }
      
      // Tentar popup apenas em localhost ou Electron
      console.log('[AUTH] Tentando popup para Microsoft (ambiente local/Electron)');
      try {
      const result = await signInWithPopup(auth, provider);
        redirectProcessedRef.current = false;
        await processAuthResult(result.user, 'microsoft');
      } catch (popupError: any) {
        // Se popup falhar, usar redirect como fallback
        console.log('[AUTH] Popup falhou para Microsoft - usando redirect');
        redirectProcessedRef.current = false;
        await signInWithRedirect(auth, provider);
        return;
      }
    } catch (error) {
      console.error('[AUTH] Erro no login com Microsoft:', error);
      throw error;
    }
  }

  async function register(email: string, password: string) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const attribution = getAttributionForFirestore();
      const newUser: UserProfile = {
        uid: result.user.uid,
        email: email,
        displayName: '',
        role: 'patient',
        clinicId: result.user.uid, // idem: clinicId == uid na criação
        isActive: true,
        lastLogin: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
        attribution,
      };
      await setDoc(doc(db, 'users', result.user.uid), newUser);
      setCurrentUser(newUser);
      setUserRole(newUser.role);
      await applyResolvedPermissions({
        uid: result.user.uid,
        email: newUser.email,
        clinicId: newUser.clinicId,
        role: newUser.role,
      });
      setClinicId(newUser.clinicId);
      
      // GA4 sign_up (once per uid) + Meta Pixel CompleteRegistration (eventID = uid)
      trackGa4SignUp(result.user.uid, 'email');
      trackCompleteRegistration(result.user.uid, 'email', getUtmContent());
      console.log('[AUTH] ✅ Attribution gravada + sign_up / CompleteRegistration disparados');
    } catch (error) {
      console.error('[AUTH] Erro no registro:', error);
      throw error;
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      setCurrentUser(null);
      redirectProcessedRef.current = false; // Resetar flag ao fazer logout
      setUserRole(null);
      setUserPermissions([]);
      setClinicId(null);
      
      // Limpar dados de sessão
      localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
      localStorage.removeItem(STORAGE_KEY_SESSION_START);
      
      // Remover listeners e parar verificação
      removeActivityListeners();
      stopActivityCheck();
      
      console.log('✅ [AUTH] Logout realizado e sessão limpa');
    } catch (error) {
      console.error('[AUTH] Erro no logout:', error);
      throw error;
    }
  }

  function getUserRedirectUrl(): string {
    if (!currentUser) return '/login';
    if (currentUser.role === 'admin') return '/admin';
    return '/dashboard';
  }

  useEffect(() => {
    // Processar resultado de redirect do Firebase Auth
    // IMPORTANTE: Processar ANTES do onAuthStateChanged para evitar loops
    const processRedirectResult = async () => {
      // NÃO verificar redirectProcessedRef aqui - SEMPRE tentar processar na primeira vez
      // A flag só será usada para evitar reprocessamento APÓS sucesso
      
      try {
        console.log('[AUTH] 🔍 Verificando resultado de redirect...');
        console.log('[AUTH] 📍 URL completa:', window.location.href);
        console.log('[AUTH] 📍 Pathname:', window.location.pathname);
        console.log('[AUTH] 📍 Search:', window.location.search);
        console.log('[AUTH] 📍 Hash:', window.location.hash);
        console.log('[AUTH] 📍 Origin:', window.location.origin);
        console.log('[AUTH] 📍 Hostname:', window.location.hostname);
        console.log('[AUTH] 📍 redirectProcessedRef:', redirectProcessedRef.current);
        
        // Verificar se há parâmetros de callback na URL ou redirect iniciado por nós
        const urlParams = new URLSearchParams(window.location.search);
        const redirectPending =
          typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('upclinic_auth_redirect') === '1';
        const hasAuthParams = urlParams.has('apiKey') || 
                             urlParams.has('state') ||
                             urlParams.has('code') ||
                             window.location.pathname.includes('__/auth/callback') ||
                             window.location.hash.includes('access_token') ||
                             redirectPending;
        
        if (hasAuthParams) {
          console.log('[AUTH] 🔍 Parâmetros de autenticação detectados na URL');
          console.log('[AUTH] 📋 Parâmetros:', {
            apiKey: urlParams.get('apiKey') ? 'presente' : 'ausente',
            state: urlParams.get('state') ? 'presente' : 'ausente',
            code: urlParams.get('code') ? 'presente' : 'ausente',
            redirectPending,
            pathname: window.location.pathname,
            hash: window.location.hash.substring(0, 50) + (window.location.hash.length > 50 ? '...' : '')
          });
        }
        
        // SÓ chamar getRedirectResult se houver evidência de redirect pendente
        // Isso evita o erro "Pending promise was never set"
        if (!hasAuthParams) {
          console.log('[AUTH] ℹ️ Nenhum parâmetro de autenticação na URL - pulando getRedirectResult');
          redirectProcessedRef.current = true;
          return;
        }
        
        console.log('[AUTH] 🔄 Chamando getRedirectResult (parâmetros detectados)...');
        const result = await getRedirectResult(auth);
        console.log('[AUTH] ✅ getRedirectResult concluído');
        console.log('[AUTH] Resultado de getRedirectResult:', result ? 'Resultado encontrado' : 'Sem resultado');
        
        if (result && result.user) {
          console.log('[AUTH] ✅ Resultado de redirect encontrado!');
          console.log('[AUTH] 📋 Detalhes:', {
            uid: result.user.uid,
            email: result.user.email,
            providerId: result.providerId,
            operationType: result.operationType
          });

          try {
            sessionStorage.removeItem('upclinic_auth_redirect');
          } catch {
            /* ignore */
          }
          
          // Marcar como processado APENAS após encontrar resultado
          redirectProcessedRef.current = true;
          
          // Processar resultado - identificar provider do redirect
          const providerId = result.providerId || 'social';
          const providerName = providerId === 'google.com' ? 'google' : 
                              providerId === 'facebook.com' ? 'facebook' : 
                              providerId === 'microsoft.com' ? 'microsoft' : 
                              'social';
          await processAuthResult(result.user, providerName);
          console.log('[AUTH] ✅ Usuário processado com sucesso via redirect');
          
          // Limpar parâmetros da URL para evitar reprocessamento
          if (hasAuthParams) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            console.log('[AUTH] 🧹 URL limpa:', cleanUrl);
          }
          
          // Navegar para dashboard
          setLoading(false);
              const redirectUrl = getUserRedirectUrl();
              console.log('[AUTH] 🚀 Navegando para:', redirectUrl);
          
          // Usar window.location.href para garantir navegação completa
          if (window.location.pathname !== redirectUrl) {
            window.location.href = redirectUrl;
          }
          
          return; // Sair após processar com sucesso
        }
        
        // Se não há resultado mas há parâmetros na URL, pode ser timing ou erro
        if (!result) {
          console.warn('[AUTH] ⚠️ Parâmetros detectados mas getRedirectResult retornou null');
          console.warn('[AUTH] ⚠️ Isso pode indicar:');
          console.warn('[AUTH]    1. Estado do redirect foi perdido');
          console.warn('[AUTH]    2. Página foi recarregada antes de processar');
          console.warn('[AUTH]    3. Problemas de configuração do Firebase');
          console.warn('[AUTH] ⚠️ Aguardando onAuthStateChanged como fallback...');
          // NÃO marcar como processado - deixar onAuthStateChanged tentar
          return;
        }
      } catch (error: any) {
        console.error('[AUTH] ❌ Erro ao processar redirect:', error);
        
        // Se for erro de "no auth event", é normal (não há redirect pendente)
        if (error.code === 'auth/no-auth-event') {
          console.log('[AUTH] ℹ️ Nenhum evento de autenticação pendente (normal)');
          redirectProcessedRef.current = true;
        } else {
          // Outros erros - marcar como processado mas logar
          console.error('[AUTH] ❌ Erro inesperado:', error.code, error.message);
          redirectProcessedRef.current = true;
        }
      }
    };
    
    // Processar redirect imediatamente, antes de qualquer outra coisa
    processRedirectResult();
    
    // Configurar persistência com IndexedDB (mais robusto) com fallback para localStorage
    console.log('🔧 [AUTH] Configurando persistência (IndexedDB → LocalStorage)...');
    setPersistence(auth, indexedDBLocalPersistence)
      .then(() => {
        console.log('✅ [AUTH] Persistência IndexedDB configurada');
      })
      .catch(err => {
        console.warn('⚠️ [AUTH] IndexedDB indisponível, usando LocalStorage:', err?.message || err);
        return setPersistence(auth, browserLocalPersistence).then(() => {
          console.log('✅ [AUTH] Persistência LocalStorage configurada');
        });
      })
      .catch(err => {
        console.error('❌ [AUTH] Falha ao configurar persistência:', err);
      });

    // Listener do Firebase Auth - será chamado quando o estado de autenticação mudar
    // Isso inclui restauração automática de sessão após refresh
    console.log('👂 [AUTH] Registrando listener de mudanças de autenticação...');
    
    // Verificar usuário atual ANTES de registrar o listener (pode já estar autenticado)
    const currentFirebaseUser = auth.currentUser;
    if (currentFirebaseUser) {
      console.log('✅ [AUTH] Usuário Firebase já encontrado no carregamento:', currentFirebaseUser.uid);
    }
    // Log removido - é normal não haver usuário no carregamento inicial
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Log apenas quando há usuário para reduzir verbosidade no console
      if (user) {
        console.log('🔄 [AUTH] onAuthStateChanged disparado:', `Usuário: ${user.uid}, Email: ${user.email}`);
        console.log('[AUTH] 📍 URL atual no onAuthStateChanged:', window.location.href);
      } else {
        console.log('🔄 [AUTH] onAuthStateChanged disparado: Sem usuário');
      }
      
      if (user) {
        // ✅ CRÍTICO: Verificar se é um subordinado que acabou de fazer login por matrícula
        // Se o currentUser já tem isSubordinate=true, não sobrescrever com dados do Firestore
        if (currentUser && currentUser.isSubordinate && currentUser.uid === user.uid) {
          console.log('✅ [AUTH] Subordinado já logado via matrícula, mantendo estado atual');
          console.log('✅ [AUTH] ClinicId do subordinado:', currentUser.clinicId);
          setLoading(false);
          return;
        }
        
        // Verificar se já estamos processando este usuário para evitar loops
        // Usar uma flag mais robusta baseada no uid do Firebase
        const processingKey = `auth_processing_${user.uid}`;
        const isProcessing = sessionStorage.getItem(processingKey);
        
        if (isProcessing && currentUser && currentUser.uid === user.uid) {
          console.log('[AUTH] Usuário já processado, ignorando onAuthStateChanged');
          setLoading(false);
          return;
        }
        
        // Marcar como processando
        sessionStorage.setItem(processingKey, 'true');
        
        // Primeiro carregar os dados do usuário
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          // Tratar erros de rede do Firestore
          if (userDoc.exists()) {
          const userData = userDoc.data() as UserProfile;
          
          // ✅ CRÍTICO: Se é subordinado e já temos currentUser, não sobrescrever
          if (currentUser && currentUser.isSubordinate && currentUser.uid === user.uid) {
            console.log('✅ [AUTH] Subordinado já logado, mantendo estado atual do onAuthStateChanged');
            setLoading(false);
            return;
          }
          
          setCurrentUser({ ...userData, uid: user.uid });
          setUserRole(userData.role);
          setClinicId(userData.clinicId);

          await applyResolvedPermissions({
            uid: user.uid,
            email: userData.email || user.email,
            clinicId: userData.clinicId,
            role: userData.role,
          });
          
          // PRIMEIRO: Criar/atualizar timestamp ANTES de verificar expiração
          // Isso garante que não expire sessões recém-restauradas
          const lastActivityStr = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
          
          if (!lastActivityStr) {
            // Primeira vez após refresh - criar timestamp agora
            const now = Date.now();
            localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, now.toString());
            localStorage.setItem(STORAGE_KEY_SESSION_START, now.toString());
            console.log('✅ [AUTH] Sessão restaurada após refresh da página - timestamp criado');
          } else {
            // Verificar se realmente expirou (com margem de segurança de 5 minutos)
            const lastActivity = parseInt(lastActivityStr, 10);
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivity;
            const SAFETY_MARGIN = 5 * 60 * 1000; // 5 minutos de margem
            
            if (timeSinceLastActivity > (INACTIVITY_TIMEOUT + SAFETY_MARGIN)) {
              // Sessão realmente expirada (mais de 3h5min sem atividade)
              console.log('⏰ [AUTH] Sessão expirada por inatividade (>3h)');
              await handleSessionExpiry();
              setLoading(false);
              return;
            } else {
              // Sessão válida - atualizar timestamp (usuário voltou)
              updateLastActivity();
              console.log(`✅ [AUTH] Sessão válida (${Math.round(timeSinceLastActivity / (1000 * 60))} min sem atividade)`);
            }
          }
          
          // Configurar listeners de atividade e verificação
          setupActivityListeners();
          startActivityCheck();
          
          // Associar subscription do Stripe em background (fire-and-forget)
          // Não bloqueia o loading — executa após a UI já estar visível
          if (user.email && !userData.stripeCustomerId) {
            const subscriptionLinkKey = `subscription_link_attempted_${user.uid}`;
            const alreadyAttempted = sessionStorage.getItem(subscriptionLinkKey);
            const isProduction = window.location.hostname !== 'localhost' &&
                                !window.location.hostname.includes('127.0.0.1') &&
                                !window.location.hostname.includes('192.168.');

            if (isProduction && !alreadyAttempted) {
              // Executar de forma assíncrona sem await — não bloqueia o login
              (async () => {
                try {
                  const functions = getFunctions(app, 'us-central1');
                  const linkSubscription = httpsCallable(functions, 'linkStripeSubscriptionByEmail');
                  const result = await linkSubscription({ email: user.email });
                  const data = result.data as any;
                  if (data?.success && data?.subscriptionsLinked > 0) {
                    const updatedUserDoc = await getDoc(doc(db, 'users', user.uid));
                    if (updatedUserDoc.exists()) {
                      setCurrentUser({ ...updatedUserDoc.data() as UserProfile, uid: user.uid });
                    }
                  }
                } catch {
                  // Erros de CORS/rede são esperados — não crítico
                } finally {
                  sessionStorage.setItem(subscriptionLinkKey, 'true');
                }
              })();
            }
          }
          
          // Verificar se acabamos de fazer login via redirect e navegar se necessário
          const urlParams = new URLSearchParams(window.location.search);
          const isAuthCallback = window.location.pathname.includes('__/auth/callback') || 
                                urlParams.has('apiKey') || 
                                urlParams.has('state') ||
                                (window.location.pathname === '/login' && !redirectProcessedRef.current);
          
          if (isAuthCallback) {
            console.log('[AUTH] 🔄 Callback detectado no onAuthStateChanged, navegando...');
            // Marcar como processado para evitar loops
            redirectProcessedRef.current = true;
            
            // Limpar parâmetros da URL
            if (urlParams.has('apiKey') || urlParams.has('state')) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            
            const redirectUrl = getUserRedirectUrl();
            if (window.location.pathname !== redirectUrl) {
              window.location.href = redirectUrl;
            }
          }
          
          // ✅ GARANTIR: currentUser está definido antes de desativar loading
          // Remover flag de processamento após sucesso
          sessionStorage.removeItem(processingKey);
          
          console.log('[AUTH] ✅ Usuário processado, currentUser definido:', {
            uid: userData.uid,
            email: userData.email,
            displayName: userData.displayName
          });
          
          setLoading(false); // Só desativar loading quando tudo estiver pronto
          } else {
            console.warn('⚠️ [AUTH] Usuário do Firebase encontrado mas perfil não existe no Firestore');
            console.warn('⚠️ [AUTH] UID:', user.uid);
            console.warn('⚠️ [AUTH] Email:', user.email);
            
            // ✅ CRÍTICO: Se é subordinado, tentar criar documento users/ se não existir
            // Verificar se é subordinado procurando em subordinate_users
            try {
              console.log('🔍 [AUTH] Verificando se é subordinado para criar documento users/...');
              console.log('🔍 [AUTH] Email do Firebase Auth:', user.email);
              console.log('🔍 [AUTH] UID do Firebase Auth:', user.uid);
              
              const subordinateQuery = query(
                collection(db, 'subordinate_users'),
                where('email', '==', user.email)
              );
              const subordinateSnapshot = await getDocs(subordinateQuery);
              
              console.log('🔍 [AUTH] Subordinate_users encontrados:', subordinateSnapshot.size);
              
              if (!subordinateSnapshot.empty) {
                const subordinateData = subordinateSnapshot.docs[0].data();
                console.log('✅ [AUTH] Subordinado encontrado, criando documento users/...');
                console.log('✅ [AUTH] Dados do subordinado:', {
                  name: subordinateData.name,
                  email: subordinateData.email,
                  clinicId: subordinateData.clinicId,
                  role: subordinateData.role
                });
                
                const subordinateUserProfile: UserProfile = {
                  uid: user.uid,
                  email: user.email || '',
                  displayName: subordinateData.name || user.email?.split('@')[0] || 'Usuário',
                  role: subordinateData.role || 'professional',
                  clinicId: subordinateData.clinicId,
                  isActive: subordinateData.status === 'ativo',
                  lastLogin: new Date(),
                  createdAt: subordinateData.createdAt || new Date(),
                  updatedAt: new Date(),
                  matricula: subordinateData.matricula,
                  adminId: subordinateData.adminId,
                  isSubordinate: true,
                  subordinateUserId: subordinateSnapshot.docs[0].id
                };
                
                console.log('🔍 [AUTH] Tentando criar documento users/ com UID:', user.uid);
                console.log('🔍 [AUTH] request.auth.uid será:', user.uid);
                console.log('🔍 [AUTH] userId no caminho será:', user.uid);
                
                // Criar documento users/
                try {
                  await setDoc(doc(db, 'users', user.uid), {
                    ...subordinateUserProfile,
                    createdAt: Timestamp.fromDate(subordinateUserProfile.createdAt),
                    updatedAt: Timestamp.fromDate(subordinateUserProfile.updatedAt),
                    lastLogin: Timestamp.fromDate(subordinateUserProfile.lastLogin)
                  });
                  
                  console.log('✅ [AUTH] ✅✅✅ DOCUMENTO users/ CRIADO COM SUCESSO ✅✅✅');
                  console.log('✅ [AUTH] UID do documento:', user.uid);
                  
                  // Verificar se foi criado
                  const verifyDoc = await getDoc(doc(db, 'users', user.uid));
                  if (verifyDoc.exists()) {
                    console.log('✅ [AUTH] Documento users/ verificado e existe!');
                  } else {
                    console.error('❌ [AUTH] Documento users/ não foi criado!');
                  }
                } catch (createError: any) {
                  console.error('❌ [AUTH] Erro ao criar documento users/:', createError.code, createError.message);
                  console.error('❌ [AUTH] Stack:', createError.stack);
                  throw createError;
                }
                
                // Definir estado
                setCurrentUser(subordinateUserProfile);
                setUserRole(subordinateUserProfile.role || 'professional');
                await applyResolvedPermissions({
                  uid: user.uid,
                  email: subordinateUserProfile.email || user.email,
                  clinicId: subordinateUserProfile.clinicId,
                  role: subordinateUserProfile.role || 'professional',
                });
                setClinicId(subordinateUserProfile.clinicId);
                setLoading(false);
                return;
              } else {
                console.warn('⚠️ [AUTH] Nenhum subordinado encontrado com email:', user.email);
              }
            } catch (subordinateError: any) {
              console.error('❌ [AUTH] Erro ao verificar/criar subordinado:', subordinateError.code, subordinateError.message);
              console.error('❌ [AUTH] Stack:', subordinateError.stack);
            }
            
            sessionStorage.removeItem(processingKey);
            setCurrentUser(null);
            setUserRole(null);
            setUserPermissions([]);
            setClinicId(null);
            setLoading(false);
          }
        } catch (firestoreError: any) {
          // Remover flag de processamento em caso de erro
          sessionStorage.removeItem(processingKey);
          
          // Tratar erros do Firestore de forma inteligente
          handleFirestoreError(firestoreError, 'AUTH', {
            suppressNetworkErrors: true,
          });
          
          // Se for erro de rede, tentar continuar (Firestore vai reconectar)
          if (isNetworkError(firestoreError)) {
            console.warn('⚠️ [AUTH] Erro de rede ao buscar perfil - tentando continuar...');
            // Manter o usuário autenticado mesmo com erro de rede temporário
            setLoading(false);
          } else {
            // Erro crítico - limpar estado
            console.error('❌ [AUTH] Erro ao buscar perfil do usuário:', firestoreError);
            setCurrentUser(null);
            setUserRole(null);
            setUserPermissions([]);
            setClinicId(null);
            setLoading(false);
          }
        }
      } else {
        // Log removido para reduzir verbosidade - é normal não haver usuário no carregamento inicial
        setCurrentUser(null);
        setUserRole(null);
        setUserPermissions([]);
        setClinicId(null);
        
        // Limpar dados de sessão quando não há usuário
        localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
        localStorage.removeItem(STORAGE_KEY_SESSION_START);
        removeActivityListeners();
        stopActivityCheck();
        setLoading(false);
      }
      
      // NÃO definir loading aqui - já foi definido dentro dos ifs acima
    });

    // Cleanup: remover listeners quando componente desmontar
    return () => {
      unsubscribe();
      removeActivityListeners();
      stopActivityCheck();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listener para atualizar currentUser quando documento mudar no Firestore
  // Diferido 3s para não concorrer com leituras do carregamento inicial
  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsubscribeFn: (() => void) | undefined;
    const timer = setTimeout(() => {
      const userDocRef = doc(db, 'users', currentUser.uid);
      unsubscribeFn = onSnapshot(
        userDocRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.data() as UserProfile;
            setCurrentUser((prevUser) => {
              if (!prevUser) return prevUser;
              return { ...userData, uid: prevUser.uid };
            });
            setUserRole((prevRole) => {
              if (userData.role !== prevRole) {
                void applyResolvedPermissions({
                  uid: currentUser.uid,
                  email: userData.email,
                  clinicId: userData.clinicId || currentUser.clinicId,
                  role: userData.role,
                });
                return userData.role;
              }
              return prevRole;
            });
            setClinicId((prevClinicId) => {
              if (userData.clinicId !== prevClinicId) {
                return userData.clinicId || null;
              }
              return prevClinicId;
            });
          }
        },
        (error: any) => {
          if (error?.code !== 'permission-denied' && !error?.message?.includes('permission')) {
            console.warn('⚠️ [AUTH] Erro no listener do documento do usuário:', error);
          }
        }
      );
    }, 3000); // Diferido 3s para não concorrer com leituras do carregamento inicial

    return () => {
      clearTimeout(timer);
      unsubscribeFn?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Apenas quando o UID mudar

  useEffect(() => {
    const loadTrial = async () => {
      if (currentUser && currentUser.uid) {
        let trial = await trialService.getTrialInfo(currentUser.uid, currentUser.email || '');
        if (!trial) {
          // Ancorar o trial no Firestore (idempotente): se já existir um trial (mesmo
          // expirado), startTrial não o recria — isso impede "resetar" o teste apenas
          // limpando o localStorage, garantindo a interrupção automática após expirar.
          try {
            await trialService.startTrial(currentUser.uid, currentUser.email || '');
            trial = await trialService.getTrialInfo(currentUser.uid, currentUser.email || '');
          } catch (anchorError) {
            console.warn('[TRIAL] Não foi possível ancorar trial no Firestore:', anchorError);
          }
        }
        if (!trial) {
          // Fallback local (ex.: offline / sem permissão) — 7 dias
          let localTrial = trialService.getTrialFromLocalStorage(currentUser.uid);
          if (!localTrial) {
            // Forçar criação do trial local
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const endDate = new Date(now);
            endDate.setDate(endDate.getDate() + 7); // 7 dias de trial
            const clinicId = 'local-fallback';
            trialService.saveTrialToLocalStorage(currentUser.uid, now, endDate, clinicId);
            localTrial = trialService.getTrialFromLocalStorage(currentUser.uid);
            console.log('[TRIAL-FALLBACK] Trial local criado:', localTrial);
          }
          if (localTrial) {
            const now = new Date();
            const timeRemaining = Math.max(0, localTrial.endTime.getTime() - now.getTime());
            const isExpired = timeRemaining <= 0;
            trial = {
              userId: currentUser.uid,
              email: currentUser.email || '',
              startTime: localTrial.startTime,
              endTime: localTrial.endTime,
              isExpired,
              timeRemaining,
              daysRemaining: Math.ceil(timeRemaining / (1000 * 60 * 60 * 24)),
              hoursRemaining: Math.floor(timeRemaining / (1000 * 60 * 60)),
              minutesRemaining: Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60))
            };
            console.log('[TRIAL-FALLBACK] trialData preenchido:', trial);
          }
        }
        setTrialData(trial);
        setLoading(false);
      } else {
        // Sem usuário ainda NÃO encerra o carregamento: quem decide é o onAuthStateChanged.
        // Antes este efeito rodava na abertura, antes de restaurar a sessão, e qualquer F5 ou
        // link salvo caía no /login → /dashboard (BUG-122).
        setTrialData(null);
      }
    };
    loadTrial();
  }, [currentUser]);

  const value = {
    currentUser,
    loading,
    userRole,
    userPermissions,
    clinicId,
    login,
    loginByMatricula,
    loginWithGoogle,
    loginWithFacebook,
    loginWithMicrosoft,
    register,
    logout,
    getUserRedirectUrl,
    hasPermission,
    refreshUserPermissions,
    hasBillingPermission,
    hasAIPermission,
    isAdmin,
    isProfessional,
    isPatient,
    resetPassword,
    trialData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
